/**
 * PayslipViewer — retrieval of an issued statement, plus the superseded banner.
 *
 * ── THE BANNER LIVES HERE BY DESIGN ──────────────────────────────────────────
 * docs/payslip-concept.md §C.4: any marking applied at viewing time —
 * "superseded", a watermark, a notice — is applied by the SERVING SURFACE and
 * NEVER by mutating the stored artefact.
 *
 * The reason is the digest. The bytes were digested at issuance and that digest
 * is the platform's proof of what the worker was given; if serving ever
 * rewrote the bytes to stamp a banner into them, the stored digest would stop
 * verifying, and it would stop verifying exactly when someone was trying to
 * establish what had been issued. A banner is presentation. The artefact is
 * evidence. So this component draws the banner in its own chrome, around an
 * iframe holding the untouched file, and the download button below hands over
 * those same bytes — a downloaded superseded payslip is byte-identical to the
 * one issued, and its digest still verifies.
 *
 * This is also why the reissue MARKER is not drawn here. That one is rendered
 * INTO the document by the backend at issuance (§D.4), because it is a
 * statement about a document that came before it and must survive printing,
 * emailing and forwarding. The UI adds nothing to the file, and
 * PayslipUI.test.tsx pins that it does not.
 */

import { useEffect, useState } from 'react'
import { AlertTriangle, Download, X } from 'lucide-react'
import { fetchPayslipDocument, downloadPayslipPdf } from '../../api'
import type { Payslip, PayslipDocument } from '../../types'
import { apiErrorMessage } from '../../../../api/client'

export default function PayslipViewer({ payslip, onClose }: {
  payslip: Payslip
  onClose: () => void
}) {
  const [doc, setDoc] = useState<PayslipDocument | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let cancelled = false
    let url: string | null = null
    // RETRIEVAL, never a re-render: this reads the stored bytes. There is no
    // code path in this UI that regenerates an issued document.
    //
    // The previous error is cleared on the SUCCESS path rather than
    // synchronously here: a setState in the effect body is the pattern the
    // repo's lint warnings track, and clearing on success is also more honest
    // — a failed fetch's message should stay up until something replaces it.
    fetchPayslipDocument(payslip.id)
      .then(d => {
        if (cancelled) { URL.revokeObjectURL(d.blobUrl); return }
        url = d.blobUrl
        setError(null)
        setDoc(d)
      })
      .catch(e => { if (!cancelled) setError(apiErrorMessage(e, 'Fetching this payslip failed')) })
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url) }
  }, [payslip.id])

  // The served status is authoritative over the list's copy: the row version
  // may have been superseded since the list was fetched.
  const superseded = (doc?.periodResultStatus ?? payslip.periodResultStatus) === 'superseded'

  async function download() {
    setBusy(true)
    try {
      await downloadPayslipPdf(payslip.id, `payslip-${payslip.taxYear}-period-${payslip.periodNumber}.pdf`)
    } catch (e) {
      setError(apiErrorMessage(e, 'Downloading this payslip failed'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-col gap-3" data-testid="payslip-viewer">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-14 font-semibold text-ink-primary">
            Payslip · {payslip.taxYear} period {payslip.periodNumber}
          </div>
          <div className="text-12 text-ink-muted mt-0.5">
            Issued {new Date(payslip.issuedAt).toLocaleDateString('en-GB')} by {payslip.issuedBy}
          </div>
        </div>
        <button onClick={onClose} aria-label="Close"
          className="w-8 h-8 flex items-center justify-center rounded-control text-ink-faint hover:bg-surface-nav-hover transition-colors flex-shrink-0">
          <X size={16} />
        </button>
      </div>

      {superseded && (
        // VIEWER CHROME. Not in the file — see the header comment.
        <div role="status" data-testid="payslip-superseded-banner"
          className="flex items-start gap-2 text-12 leading-relaxed rounded-control border border-edge-control bg-surface-control px-3 py-2 text-ink-body">
          <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
          <span>
            The period this statement covers has since been restated, so this document
            is superseded. It remains the statement that was issued, and is kept
            exactly as issued — this notice is shown here, not written into the file.
          </span>
        </div>
      )}

      {error && (
        <div role="alert" data-testid="payslip-viewer-error"
          className="text-12 leading-relaxed rounded-control border border-edge-control bg-surface-control px-3 py-2 text-ink-body">
          {error}
        </div>
      )}

      {doc && (
        <>
          <div className="text-11 text-ink-muted tabular-nums break-all">
            sha256 {doc.digestSha256}
          </div>
          <iframe title="Payslip" src={doc.blobUrl} data-testid="payslip-frame"
            className="w-full h-[28rem] border border-edge-control rounded-tile bg-surface-control" />
        </>
      )}

      <div>
        <button onClick={download} disabled={busy} data-testid="payslip-download"
          className="inline-flex items-center gap-1.5 px-3 py-2 text-12 font-semibold rounded-control border border-edge-control bg-surface-control text-ink-body transition-colors hover:bg-surface-nav-hover disabled:opacity-50">
          <Download size={14} /> {busy ? 'Downloading…' : 'Download'}
        </button>
      </div>
    </div>
  )
}
